import { type SelectionRange } from '../../machine/state/types';
import { createEffect } from '../utils/create-effect';

import {
  APP_ELEMENT_ID,
  HAS_ACTIVE_HIGHLIGHT_CLASS,
  SCROLLABLE_INNER_CONTAINER_ID,
} from './constants';
import { FocusRenderer } from './renderers/focus-renderer';
import { HighlightsRenderer } from './renderers/highlights-renderer';
import { createHighlightsContainerElement } from './utils/create-highlights-container-element';
import { type HighlightStyleManager } from './utils/highlight-style-manager';

/**
 * Creates the main effect that draws / renders highlights, focus, etc.
 */
export const createHighlightsEffect = createEffect(
  (styleManager: HighlightStyleManager) => {
    // ---------------------------------------------------------------------------
    // DOM
    // ---------------------------------------------------------------------------

    /**
     * The internal highlights container, where focus, highlights, etc. are drawn and positioned.
     */
    let highlightsContainer: HTMLElement | null = null;

    /**
     * The el with the id SCROLLABLE_INNER_CONTAINER_ID, which is the main
     * scrolling container on the databases app, rendered in react.
     */
    let scrollableContainer: HTMLElement | null = null;

    /**
     * The mutation observer, which listens to the DOM and waits for the
     * el with the id SCROLLABLE_INNER_CONTAINER_ID to mount. Without this,
     * the highlightsContainer has nothing to attach itself to.
     */
    let observer: MutationObserver | null = null;

    // ---------------------------------------------------------------------------
    // State
    // ---------------------------------------------------------------------------

    /**
     * We modify the position to be relative when this setup() is run.
     * We cache the original so we can roll it back if there was one.
     */
    let originalPosition: string | null = null;

    // ---------------------------------------------------------------------------
    // Renderers
    // ---------------------------------------------------------------------------

    /**
     * This map contains a reference of a SelectionRange['id'] to an active HTMLElement;
     */
    const highlightElements = new Map<SelectionRange['id'], HTMLElement>();

    /**
     * The renderer responsible for creating, updating, and removing the blue selection
     * highlight elements in the DOM.
     */
    let highlightsRenderer: HighlightsRenderer | null = null;

    /**
     * The renderer that is responsible for creating, updating, and removing the blue
     * focus box.
     */
    let focusRenderer: FocusRenderer | null = null;

    function attemptMountingHighlightsContainerToScrollContainer() {
      if (!highlightsContainer) {
        return;
      }

      const scrollableInnerContainer = document.getElementById(
        SCROLLABLE_INNER_CONTAINER_ID
      );

      if (
        scrollableInnerContainer &&
        highlightsContainer.parentElement !== scrollableInnerContainer
      ) {
        originalPosition = scrollableInnerContainer.style.position || null;
        scrollableInnerContainer.style.position = 'relative';
        scrollableInnerContainer.appendChild(highlightsContainer);
        scrollableContainer = scrollableInnerContainer;

        // Initialize the highlightsRenderer now that we have the containers
        highlightsRenderer = new HighlightsRenderer(
          highlightsContainer,
          scrollableContainer,
          highlightElements
        );

        // Initialize the focusRenderer now that we have the els
        focusRenderer = new FocusRenderer(highlightsContainer, scrollableContainer);
      }
    }

    return {
      deltaTypes: ['selectionModified', 'selectionReplaced', 'focusChanged'],

      setup() {
        styleManager.add();

        // create the core elements in memory
        highlightsContainer = createHighlightsContainerElement();

        // setup the MutationObserver to attempt attaching / mounting our highlights container
        observer = new MutationObserver(() =>
          attemptMountingHighlightsContainerToScrollContainer()
        );

        // kick off the observer / watch for changes
        const listenerRoot = document.getElementById(APP_ELEMENT_ID) || document.body;

        observer.observe(listenerRoot, { childList: true, subtree: true });

        // attempt to mount (in case React is already ready)
        attemptMountingHighlightsContainerToScrollContainer();
      },

      dispose() {
        if (observer) {
          observer.disconnect();
          observer = null;
        }

        // Clean up the highlightsRenderer and its elements
        if (highlightsRenderer) {
          highlightsRenderer.clear();
          highlightsRenderer = null;
        }

        // Clean up the focusRenderer and its elements
        if (focusRenderer) {
          focusRenderer.clear();
          focusRenderer = null;
        }

        if (highlightsContainer) {
          highlightsContainer.remove();
          highlightsContainer = null;
        }

        styleManager.remove();

        if (scrollableContainer) {
          // Remove any stale HAS_ACTIVE_HIGHLIGHT_CLASS class from the scroll container
          scrollableContainer.classList.remove(HAS_ACTIVE_HIGHLIGHT_CLASS);

          if (originalPosition === null) {
            scrollableContainer.style.removeProperty('position');
          } else {
            scrollableContainer.style.position = originalPosition;
          }

          scrollableContainer = null;
          originalPosition = null;
        }
      },

      onDelta(delta, state) {
        // We need the manager for selections, and the elements for focus.
        if (!highlightsRenderer || !focusRenderer || !scrollableContainer) {
          return;
        }

        // Handle the focus first (the blue box)
        // Focus can change on its own event, or as part of a selection replacement
        const focusChange = delta.kind === 'focusChanged' ? delta : delta.focusChange;

        if (focusChange) {
          focusRenderer.update(focusChange.next);
        }

        // Handle Selections (The Blue Backgrounds)

        // Case A: Full Replacement (e.g., Click, Clear)
        if (delta.kind === 'selectionReplaced') {
          highlightsRenderer.clear();

          for (const range of delta.selections) {
            highlightsRenderer.add(range, state.mode);
          }

          return;
        }

        // Case B: Delta Updates (e.g., Drag, Resize, Cmd+Click)
        if (delta.kind === 'selectionModified') {
          if (delta.rangeIdsToRemove) {
            for (const id of delta.rangeIdsToRemove) {
              highlightsRenderer.remove(id);
            }
          }

          if (delta.added) {
            for (const range of delta.added) {
              highlightsRenderer.add(range, state.mode);
            }
          }

          if (delta.updated) {
            for (const range of delta.updated) {
              highlightsRenderer.update(range, state.mode);
            }
          }
        }
      },
    };
  }
);
